import { ClassicEditor } from 'ckeditor5'
import { useCallback, useRef } from 'react'
import { v4 as newGuid } from 'uuid'
import {
    useContentItemViewNodes,
    useWorkspace2Actions,
} from '../Workspace2Store'
import { useOptimistic } from '../hooks'
import { UpdateFunction } from '../hooks/useOptimistic'
import { ContentItemViewNodesType } from '../types/contentItemViewNode.type'
import { useNotification } from './notificationService'

/** Metadata for a transaction snapshot */
type SnapshotMetaData = Record<string, unknown>

/**
 * Transaction snapshot interface for tracking tree state changes
 */
export interface TreeTransactionSnapshot {
    /** Unique transaction ID */
    id: string
    /** Timestamp of the snapshot */
    timestamp: number
    /** State of the tree at the time of the snapshot */
    state: Partial<ContentItemViewNodesType>[]
    /** Additional metadata attached to the snapshot */
    metadata?: SnapshotMetaData
}

/**
 * A service for managing updates on workspace tree structure.
 *
 * @remarks
 * This service is used to manage optimistic updates and transactions
 * with snapshot capabilities to enable advanced rollback mechanisms without
 * the need to reload the page.
 *
 * @returns Transaction manager operations
 */
export const useTransactionManager = () => {
    const notification = useNotification()
    const contentItemViewNodes = useContentItemViewNodes()
    const { setContentItemViewNodes } = useWorkspace2Actions()

    const snapshotsRef = useRef<TreeTransactionSnapshot[]>([])
    const pendingTransactionsRef = useRef<Set<string>>(new Set())
    const [_, runOptimistic, editorRef] = useOptimistic(notification)

    /**
     * Creates a new snapshot of the current tree state
     * @param metadata Additional metadata to attach to the snapshot
     * @returns Transaction snapshot object with unique ID
     */
    const createSnapshot = useCallback(
        (metadata?: Record<string, any>): TreeTransactionSnapshot => {
            // create deep clone of tree structure (contentItemViewNodes) to ensure snapshot is immutable
            const clonedState = structuredClone(contentItemViewNodes)

            const snapshot = {
                id: `txn-${newGuid()}`,
                timestamp: Date.now(),
                state: clonedState,
                metadata,
            }

            // add to history, keeping the last 20 snapshots
            snapshotsRef.current = [snapshot, ...snapshotsRef.current].slice(
                0,
                20
            )
            return snapshot
        },
        [contentItemViewNodes]
    )

    /**
     * Begins a transaction by creating a snapshot
     * @param metadata Transaction metadata
     * @returns Transaction ID
     */
    const beginTransaction = useCallback(
        (metadata?: SnapshotMetaData): string => {
            const snapshot = createSnapshot(metadata)
            pendingTransactionsRef.current.add(snapshot.id)

            return snapshot.id
        },
        [createSnapshot]
    )

    /**
     * Commits a transaction by removing it from the pending list
     * @param transactionId Transaction ID to commit
     */
    const commit = useCallback((transactionId: string): void => {
        if (pendingTransactionsRef.current.has(transactionId)) {
            pendingTransactionsRef.current.delete(transactionId)
        }
    }, [])

    /**
     * Rolls back a transaction to its snapshot state by transaction id.
     *
     * @param transactionId Transaction ID to rollback
     * @returns The original state from the snapshot, or null if not found
     */
    const rollback = useCallback(
        (transactionId: string): Partial<ContentItemViewNodesType>[] | null => {
            const snapshot = snapshotsRef.current.find(
                (s) => s.id === transactionId
            )

            if (snapshot) {
                // rollback tree structure using snapshot
                setContentItemViewNodes(snapshot.state)
                // remove transaction from pending list
                pendingTransactionsRef.current.delete(transactionId)
                return snapshot.state
            }

            return null
        },
        [notification]
    )

    /**
     * Executes an optimistic update with transaction management.
     *
     * @param options Update options
     * @param options.fallbackState The fallback state to use in case of an error
     * @param options.asyncAction The async action to perform
     * @param options.successCbFn (optional) Callback to run on success
     * @param options.errorCbFn (optional) Callback to run on error
     */
    const executeWithOptimistic: UpdateFunction<
        string | Partial<ContentItemViewNodesType>[]
    > & {
        /** The optimistic tree structure to apply */
        optimisticTreeViewNodes?: Partial<ContentItemViewNodesType>[]
    } = useCallback(
        async ({ fallbackState, asyncAction, successCbFn, errorCbFn }) => {
            const transactionId = beginTransaction({ fallbackState })

            // updates UI immediately, then perform async mutation
            await runOptimistic({
                fallbackState,
                asyncAction,
                // onSuccess: rehydrate contentItemViewNodes with the changes
                successCbFn: () => {
                    commit(transactionId)
                    successCbFn?.()
                },
                // onError: (optional) rollback contentItemViewNodes to previous state
                errorCbFn: () => {
                    rollback(transactionId)
                    errorCbFn?.()
                },
            })
        },
        [runOptimistic, beginTransaction, commit, rollback]
    )

    /**
     * Registers the editor reference for optimistic updates
     *
     * @param editor CKEditor instance
     */
    const registerEditorRef = useCallback(
        <T extends ClassicEditor>(editor: T) => {
            editorRef.current = editor
        },
        []
    )

    return {
        executeWithOptimistic,
        registerEditorRef,
    }
}
